import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import { codeFont } from '../../utils/fonts';
import { formatPriceWithCurrency } from '../../utils/vat';

/**
 * Pick which live part stands in for a deleted one on a copied package row.
 *
 * `row._candidates` comes from POST /api/service-packages/from-work-order/: register
 * parts (ready to use) and catalogue items (materialised into a Part only when chosen,
 * through the same endpoint the part picker uses). `onPick(candidate, part)` receives
 * the chosen candidate and the live Part row to put on the package row.
 */
function ReplacementCandidatesDialog({ open, onClose, row, onPick }) {
  const { t } = useTranslation();
  const candidates = row?._candidates || [];
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    const current = candidates.findIndex((c) => c.selected);
    setSelectedIndex(current >= 0 ? current : 0);
    setBusy(false);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row?._key]);

  const handleUse = async () => {
    const candidate = candidates[selectedIndex];
    if (!candidate) return;
    setBusy(true);
    setError(null);
    try {
      let part;
      if (candidate.kind === 'catalogue' && !candidate.part_id) {
        const res = await axios.post(`/api/catalogue-items/${candidate.catalogue_item_id}/materialize/`);
        part = res.data?.part;
        if (!part) throw new Error('no part');
      } else {
        part = {
          id: candidate.part_id,
          name: candidate.name,
          manufacturer_code: candidate.manufacturer_code,
          manufacturer_name: candidate.manufacturer_name,
          unit: candidate.unit,
          primary_identifier_code: '',
        };
      }
      onPick(candidate, part);
    } catch {
      setError(t('huoltopaketit.candidateSwitchFailed'));
    } finally {
      setBusy(false);
    }
  };

  const priceLine = (candidate) => {
    const bits = [];
    if (candidate.price != null) bits.push(`${t('huoltopaketit.purchasePriceShort')} ${formatPriceWithCurrency(candidate.price)}`);
    if (candidate.sell_price != null) bits.push(`${t('huoltopaketit.sellPriceShort')} ${formatPriceWithCurrency(candidate.sell_price)}`);
    return bits.join(' · ');
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" data-testid="replacement-candidates-dialog">
      <DialogTitle>{t('huoltopaketit.candidatesTitle')}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('huoltopaketit.candidatesOriginal', {
            name: [row?._original?.name, row?._original?.manufacturer_code].filter(Boolean).join(' · '),
          })}
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        <List disablePadding>
          {candidates.map((candidate, index) => (
            <ListItemButton
              key={`${candidate.kind}-${candidate.part_id || candidate.catalogue_item_id}`}
              selected={index === selectedIndex}
              onClick={() => setSelectedIndex(index)}
              disabled={busy}
              sx={{ borderRadius: '10px', mb: 0.5, alignItems: 'flex-start', flexWrap: 'wrap' }}
              data-testid="replacement-candidate"
            >
              <ListItemText
                primary={candidate.name}
                secondary={(
                  <Box component="span" sx={{ display: 'block' }}>
                    <Box component="span" sx={{ ...codeFont }}>{candidate.manufacturer_code}</Box>
                    {candidate.manufacturer_name ? ` · ${candidate.manufacturer_name}` : ''}
                    {priceLine(candidate) ? <><br />{priceLine(candidate)}</> : null}
                  </Box>
                )}
                primaryTypographyProps={{ fontWeight: 500 }}
                sx={{ mr: 1 }}
              />
              <Chip
                size="small"
                variant="outlined"
                label={candidate.kind === 'register' ? t('huoltopaketit.candidateRegister') : candidate.source}
                sx={{ mt: 0.75 }}
              />
            </ListItemButton>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t('Cancel')}</Button>
        <Button variant="contained" onClick={handleUse} disabled={busy || !candidates.length} data-testid="replacement-candidate-use">
          {busy ? <CircularProgress size={20} /> : t('huoltopaketit.candidateUse')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ReplacementCandidatesDialog;
